import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './css/NewPred.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX } from '@fortawesome/free-solid-svg-icons';
import labService from './labService';
import Visualization from './Visualization.js';

// Import SVG images
import sphericalIcon from './images/shapes/sphere.png';
import cubicIcon from './images/shapes/cube.png';
import polyhedralIcon from './images/shapes/polyhedral.png';
import rodIcon from './images/shapes/rod.png';
import tubeIcon from './images/shapes/tube.png';
import wiresIcon from './images/shapes/wire.png';
import plateletIcon from './images/shapes/platelet.png';
import inconclusiveIcon from './images/shapes/inconclusive.png';
import sphereLight from './images/shapes/sphere_light.png';
import cubeLight from './images/shapes/cube_light.png';
import polyLight from './images/shapes/polyhedral_light.png';
import rodLight from './images/shapes/rod_light.png';
import tubeLight from './images/shapes/tube_light.png';
import wireLight from './images/shapes/wire_light.png';
import plateletLight from './images/shapes/platelet_light.png';
import inconLight from './images/shapes/inconclusive_light.png';

const NewPred = () => {
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [visualizationType, setVisualizationType] = useState('inconclusive');
    const [descriptorValues, setDescriptorValues] = useState({});
    const [modelDescriptors, setModelDescriptors] = useState(null);
    const [selectedCrystalStructure, setSelectedCrystalStructure] = useState(''); // NOWE
    // Modyfikacja rdzenia i funkcjonalizacja powierzchni. Modele PhysChem ich
    // uzywaja (np. zeta potential liczy coating = 1, gdy powierzchnia jest
    // funkcjonalizowana), wiec bez tych pol licza sie na domyslnym "brak".
    const CORE_OPTIONS = ['No modification', 'Ag', 'Au', 'Pt', 'Other'];
    const SURFACE_OPTIONS = ['No modification', 'PEG', 'PDA', 'Plasma', 'Other'];
    const [selectedCore, setSelectedCore] = useState('No modification');
    const [selectedSurface, setSelectedSurface] = useState('No modification');
    const npContentRef = useRef(null);

    // Shape configuration with icons
    const shapeConfig = {
        spherical: { icon: sphericalIcon, icon_light: sphereLight, color: '#1b4581' },
        cubic: { icon: cubicIcon, icon_light: cubeLight, color: '#1b4581' },
        polyhedral: { icon: polyhedralIcon, icon_light: polyLight, color: '#1b4581' },
        rod: { icon: rodIcon, icon_light: rodLight, color: '#1b4581' },
        tube: { icon: tubeIcon, icon_light: tubeLight, color: '#1b4581' },
        wires: { icon: wiresIcon, icon_light: wireLight, color: '#1b4581' },
        platelet: { icon: plateletIcon, icon_light: plateletLight, color: '#1b4581' },
        inconclusive: { icon: inconclusiveIcon, icon_light: inconLight, color: '#1b4581' }
    };

    // Fetch model descriptors when component loads
    useEffect(() => {
        const fetchModelDescriptors = async () => {
            try {
                const token = JSON.parse(localStorage.getItem('user'))?.access;
                const selectedModelsString = localStorage.getItem('selectedModels');
                
                if (!token || !selectedModelsString) {
                    setError("No models selected or you must be logged in.");
                    return;
                }
                
                const selectedModelsObj = JSON.parse(selectedModelsString);
                const modelIds = Object.keys(selectedModelsObj).map(id => parseInt(id));

                if (modelIds.length === 0) {
                    setError("No models selected.");
                    return;
                }

                const response = await labService.getModelDescriptors(token, modelIds);
                setModelDescriptors(response.data);
                
                // Ustaw domyślną wartość crystal structure jeśli dostępna
                if (response.data.crystal_structure && 
                    response.data.crystal_structure.available_structures && 
                    response.data.crystal_structure.available_structures.length > 0) {
                    const defaultStructure = response.data.crystal_structure.default || 
                                           response.data.crystal_structure.available_structures[0];
                    setSelectedCrystalStructure(defaultStructure);
                }
                
                setDescriptorValues({});
                
            } catch (error) {
                console.error('Failed to fetch model descriptors:', error);
                setError('Failed to fetch model descriptors. Please try again.');
            }
        };

        fetchModelDescriptors();
    }, []);

    const handleGoBack = () => navigate('/home');

    const handleDescriptorChange = (frontendKey, value) => {
        setDescriptorValues(prev => ({
            ...prev,
            [frontendKey]: value
        }));
    };

    const validateDescriptors = () => {
        if (!modelDescriptors || !modelDescriptors.descriptors2frontend || modelDescriptors.descriptors2frontend.length === 0) {
            return true;
        }
        
        for (let i = 0; i < modelDescriptors.descriptors2frontend.length; i++) {
            const key = modelDescriptors.descriptors2frontend[i];
            const value = descriptorValues[key];
            
            if (!value || value.trim() === '') {
                return false;
            }
            
            const numValue = parseFloat(value);
            if (isNaN(numValue) || numValue < 0) {
                return false;
            }
        }
        return true;
    };

    const validateCrystalStructure = () => {
        // Jeśli crystal_structure jest wymagana (dostępna), musi być wybrana
        if (modelDescriptors?.crystal_structure && 
            modelDescriptors.crystal_structure.available_structures && 
            modelDescriptors.crystal_structure.available_structures.length > 0) {
            return selectedCrystalStructure !== '';
        }
        return true; // Jeśli nie jest wymagana, walidacja przechodzi
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitted(true);


        if (!validateDescriptors()) {
            setError("Please fill all descriptor fields with valid numbers.");
            return;
        }

        if (!validateCrystalStructure()) {
            setError("Please select a crystal structure.");
            return;
        }

        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.access) {
            setError("You must be logged in to create an analysis.");
            return;
        }

        const token = user.access;
        const selected_models = JSON.parse(localStorage.getItem('selectedModels'));
        const endpoint_type = JSON.parse(localStorage.getItem('selectedEndpoint'));
        const anal_name = JSON.parse(localStorage.getItem('analysisName'));

        const data = {
            name: anal_name,
            visualization_type: visualizationType,
            descriptors: descriptorValues,
            endpoint_type,
            selected_models,
            selected_crystal_structure: selectedCrystalStructure || null, // NOWE
            core_modification: selectedCore === 'No modification' ? null : selectedCore,
            surface_functionalization: selectedSurface === 'No modification' ? null : selectedSurface,
        };

        try {
            const response = await labService.createAnalysis(data, token);

            const analysisId = response?.data?.analysis_id;
            if (analysisId) {
                navigate(`/laboratory/summary/${analysisId}`);
            } else {
                setError("An error occurred while creating the analysis. Please try again.");
            }
        } catch (error) {
            console.error("Create analysis error:", error);
            setError("An error occurred while creating the analysis. Please try again.");
        }
    };

    const renderShapeButton = (shape, displayName = null) => {
        const config = shapeConfig[shape];
        const isActive = visualizationType === shape;
        const name = displayName || shape.charAt(0).toUpperCase() + shape.slice(1);
        
        return (
            <button
                key={shape}
                onClick={() => setVisualizationType(shape)}
                type="button"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: isActive ? config.color : '#fafcff',
                    color: isActive ? 'white' : '#1b4581',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: isActive ? 'bold' : 'normal',
                    minWidth: '130px'
                }}
            >
                <img 
                    src={isActive ? config.icon_light : config.icon} 
                    alt={`${name} shape`}
                    style={{ 
                        width: '20px', 
                        height: '20px',
                        
                    }} 
                />
                {name}
            </button>
        );
    };

    const renderNanoformSelect = (id, label, value, setValue, options) => (
        <div className='input-wrap' key={id}>
            <div className='input-cont'>
                <label htmlFor={id}>{label}:</label>
                <select
                    id={id}
                    className='input-anal-name'
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    style={{
                        padding: '8px', border: '1px solid #ccc', borderRadius: '4px',
                        fontSize: '14px', width: '100%'
                    }}
                >
                    {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
            </div>
        </div>
    );

    // NOWA FUNKCJA - renderowanie crystal structure selector
    const renderCrystalStructureSelector = () => {
        if (!modelDescriptors?.crystal_structure || 
            !modelDescriptors.crystal_structure.available_structures || 
            modelDescriptors.crystal_structure.available_structures.length === 0) {
            return null;
        }

        const hasError = isSubmitted && selectedCrystalStructure === '';
        const availableStructures = modelDescriptors.crystal_structure.available_structures;

        return (
                <div className='input-wrap'>
                    <div className='input-cont'>
                        <label 
                            className={hasError ? 'error-input' : ''} 
                            htmlFor="crystal-structure"
                        >
                            Select Crystal Structure:
                        </label>
                        <select
                            id="crystal-structure"
                            className='input-anal-name'
                            value={selectedCrystalStructure}
                            onChange={(e) => setSelectedCrystalStructure(e.target.value)}
                            style={{
                                padding: '8px',
                                border: hasError ? '2px solid #ff0000' : '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '14px',
                                width: '100%'
                            }}
                        >
                            <option value="">-- Select Structure --</option>
                            {availableStructures.map((structure) => (
                                <option key={structure} value={structure}>
                                    {structure}
                                </option>
                            ))}
                        </select>
                    </div>
                    {hasError && (
                        <div className='anal-error-div'>
                            <p className='anal-error'>
                                Crystal structure selection is required
                            </p>
                        </div>
                    )}
                </div>
        );
    };

    const renderDescriptorFields = () => {
        if (!modelDescriptors || !modelDescriptors.descriptors || modelDescriptors.descriptors.length === 0) {
            return (
                <div className='descriptor-section' style={{ marginTop: '20px' }}>
                    <p style={{ color: '#666', fontStyle: 'italic' }}>
                        No descriptors available for selected models.
                    </p>
                </div>
            );
        }

        return (
            <div className='descriptor-section'>
                    {/* NOWE - Crystal Structure Selector */}
                    {renderCrystalStructureSelector()}
                    {renderNanoformSelect('core-modification', 'Core Modification',
                        selectedCore, setSelectedCore, CORE_OPTIONS)}
                    {renderNanoformSelect('surface-functionalization', 'Surface Functionalization',
                        selectedSurface, setSelectedSurface, SURFACE_OPTIONS)}
                {modelDescriptors.descriptors.map((descriptor, index) => {
                    const frontendKey = modelDescriptors.descriptors2frontend[index];
                    const unit = modelDescriptors.descriptors_units[index];
                    const value = descriptorValues[frontendKey] || '';
                    const hasError = isSubmitted && (!value || value.trim() === '' || isNaN(parseFloat(value)) || parseFloat(value) < 0);

                    return (
                        <div key={frontendKey} className='input-wrap' >
                            <div className='input-cont'>
                                <label 
                                    className={hasError ? 'error-input' : ''} 
                                    htmlFor={frontendKey}
                                >
                                    {descriptor} ({unit}):
                                </label>
                                <input
                                    className='nqtb-input'
                                    type="number"
                                    step="any"
                                    min="0"
                                    id={frontendKey}
                                    value={value}
                                    placeholder={`Enter ${descriptor.toLowerCase()}`}
                                    onChange={(e) => handleDescriptorChange(frontendKey, e.target.value)}
                                    required
                                />
                            </div>
                            {hasError && (
                                <div className='anal-error-div'>
                                    <p className='anal-error'>
                                        Valid {descriptor.toLowerCase()} is required
                                    </p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className='new-pred-container'>
            <div className='np-tabs'>
                <div className='x'>
                    <button onClick={handleGoBack} className='x-but'>
                        <FontAwesomeIcon className='icon-x' icon={faX} />
                    </button>
                </div>
                <div className='np-steps done-step'><p className='p-step'>STEP 1</p><p className='mar-btm-few'>Select endpoints</p></div>
                <div className='np-steps active-tab'><p className='p-step'>STEP 2</p><p className='mar-btm-few'>Define Nanoform</p></div>
                <div className='np-steps'><p className='p-step'>STEP 3</p><p className='mar-btm-few'>Summary</p></div>
                <div className='np-steps'><p className='p-step'>STEP 4</p><p className='mar-btm-few'>Results</p></div>
            </div>

            <div className='models-cont' ref={npContentRef}>

                <div className='upper-cp-vis'>
                    <div className='control-panel'>
                        <p className="end-p">Shape</p>
                        <form className='anal-form'>
                            

                            <div className="shape-section">

                                <div className="shape-columns">
                                    
                                    {/* LEFT COLUMN */}
                                    <div className="shape-column">

                                        {/* Spheroidal */}
                                        <div className="shape-group">
                                            <h4 className="shape-title">SPHEROIDAL</h4>
                                            <div className="shape-buttons">
                                                {renderShapeButton('spherical')}
                                                {renderShapeButton('cubic')}
                                                {renderShapeButton('polyhedral')}
                                            </div>
                                        </div>

                                        {/* Platelet */}
                                        <div className="shape-group">
                                            <h4 className="shape-title">PLATELET</h4>
                                            <div className="shape-buttons">
                                                {renderShapeButton('platelet')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* RIGHT COLUMN */}
                                    <div className="shape-column">

                                        {/* Elongated */}
                                        <div className="shape-group">
                                            <h4 className="shape-title">ELONGATED</h4>
                                            <div className="shape-buttons">
                                                {renderShapeButton('rod')}
                                                {renderShapeButton('tube')}
                                                {renderShapeButton('wires')}
                                            </div>
                                        </div>

                                        {/* Other */}
                                        <div className="shape-group">
                                            <h4 className="shape-title">OTHER</h4>
                                            <div className="shape-buttons">
                                                {renderShapeButton('inconclusive', 'Inconclusive')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Dynamic Descriptor Fields */}
                            {renderDescriptorFields()}

                            {/* Error Display */}
                            {error && (
                                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#ffe6e6', border: '1px solid #ff0000', borderRadius: '4px' }}>
                                    <p style={{ color: '#ff0000', margin: '0' }}>{error}</p>
                                </div>
                            )}

                            
                        </form>
                    </div>

                    <div className='visualization-area' style={{ flex: '2' }}>
                        <Visualization
                            core={{ name: 'TiO2' }}
                            coreModification={null}
                            coreColor="#b6b6b6" 
                            surfaceFunctionalization={false}
                            surfaceFunctionalizationColor="#9ed2ff"
                            lineColor="#7fb13a"
                            isNanoformImageGenerated={false}
                            visualizationType={visualizationType}
                        />
                    </div>
                </div>

                <div className='next-div'>
                    <button type="submit" onClick={handleSubmit} className={`next-but-ready`}>
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewPred;
